// The place to drop several documents at once, above "Procesar el día"'s rows. It only collects
// the files; which row each belongs to is `asignarArchivos` (`utils/cierreCargaAgrupada.js`).
//
// Same shape as `ProcesoDelDia`'s rows: the input is kept for the file dialog and screen readers
// but hidden, and the whole box is its `<label>`, which also takes dragged files.
//
// B-70 — it also takes the reports' folder: dropped (walked with `archivosDeEntradas`) or chosen
// with "Elegir carpeta" (a second input with `webkitdirectory`). From a folder only the files of
// the slots' format go on (`admiteArchivo`), so its other files are not reported as unrecognised.
import { useState } from "react";
import { FolderOpen, Files } from "lucide-react";
import { cn } from "@mantis-sas/ui";

import { admiteArchivo, archivosDeEntradas } from "../../../utils/cierreCargaAgrupada";
import { guia } from "../../../utils/cierreTours";

// `etiqueta`, when given, names the two inputs for screen readers (and tests) apart from the title.
export function ZonaArchivos({
  id, titulo, ayuda, accept, etiqueta, deshabilitada = false, onArchivos,
}) {
  const [encima, setEncima] = useState(false);

  const recibir = (lista, { deCarpeta = false } = {}) => {
    const archivos = Array.from(lista || []).filter((archivo) => !deCarpeta || admiteArchivo(archivo.name, accept));
    if (!deshabilitada && archivos.length) onArchivos(archivos);
  };

  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    if (deshabilitada) return;
    // The entries have to be taken now: the drop's data is gone once the handler returns.
    const entradas = Array.from(e.dataTransfer?.items || [])
      .map((item) => (typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null))
      .filter(Boolean);
    if (entradas.some((entrada) => entrada.isDirectory)) {
      archivosDeEntradas(entradas)
        .then((archivos) => recibir(archivos, { deCarpeta: true }))
        .catch(() => {});
      return;
    }
    recibir(e.dataTransfer?.files);
  };

  return (
    <div
      {...guia("Archivos", "Arrastra aquí los archivos, una carpeta o un zip, o usa los botones para elegirlos. Cada archivo se reconoce por su nombre.")}
      onDragOver={(e) => {
        e.preventDefault();
        if (!deshabilitada) setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={soltar}
      className={cn(
        "flex items-center gap-2 rounded-[10px] border border-dashed bg-card pr-2 transition-colors",
        deshabilitada ? "opacity-60" : "hover:border-brinks-border-strong",
        encima && "border-brinks-border-strong bg-brinks-surface"
      )}
    >
      <label
        htmlFor={id}
        className={cn("flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2", deshabilitada ? "cursor-not-allowed" : "cursor-pointer")}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-brinks-surface text-brinks-muted-foreground">
          <Files className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-[13px] sm:truncate font-medium text-brinks-foreground">{titulo}</span>
          <span className="block text-[11.5px] text-brinks-muted-foreground">{ayuda}</span>
        </span>
      </label>
      <label
        htmlFor={`${id}-carpeta`}
        className={cn(
          "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[7px] border bg-card px-2.5 text-[12px] font-medium text-brinks-foreground transition",
          deshabilitada ? "cursor-not-allowed" : "cursor-pointer hover:bg-brinks-surface"
        )}
      >
        <FolderOpen className="h-3.5 w-3.5" /> Elegir carpeta
      </label>
      <input
        id={id}
        type="file"
        multiple
        accept={accept}
        aria-label={etiqueta}
        disabled={deshabilitada}
        onChange={(e) => {
          recibir(e.target.files);
          // The same files chosen again must fire again.
          e.target.value = "";
        }}
        className="sr-only"
      />
      <input
        id={`${id}-carpeta`}
        type="file"
        multiple
        webkitdirectory=""
        aria-label={etiqueta ? `${etiqueta} (carpeta)` : undefined}
        disabled={deshabilitada}
        onChange={(e) => {
          recibir(e.target.files, { deCarpeta: true });
          e.target.value = "";
        }}
        className="sr-only"
      />
    </div>
  );
}
